if (!customElements.get("wirenco-cut-picker")) {
  customElements.define(
    "wirenco-cut-picker",
    class WirencoCutPicker extends HTMLElement {
      static selections = new Map();
      static pendingAdd = false;
      static uncertainAdd = false;

      connectedCallback() {
        if (this.initialized) return;
        this.form = this.closest("form");
        const configuration = this.querySelector("[data-wc-config]");
        if (!this.form || !configuration) return;
        this.config = JSON.parse(configuration.textContent);
        this.strings = this.config.strings;
        this.rows = this.querySelector("[data-wc-rows]");
        this.template = this.querySelector("[data-wc-row-template]");
        this.error = this.querySelector("[data-wc-error]");
        this.status = this.querySelector("[data-wc-status]");
        this.submit = this.form.querySelector('[type="submit"]');
        this.selectionKey = `${this.form.id}:${this.config.product_id || ""}`;
        this.money = new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency: this.config.currency || "USD",
        });
        const saved = this.constructor.selections.get(this.selectionKey);
        if (saved?.length) {
          this.rows.replaceChildren();
          saved.forEach((values) => this.appendRow(values));
        } else {
          const firstLength = this.rows.querySelector("[data-wc-length]");
          if (firstLength) firstLength.value = String(this.getVariant()?.minimum || 1);
        }
        this.onInput = (event) => {
          if (!event.target.matches("[data-wc-length], [data-wc-pieces]")) return;
          this.showError();
          if (this.status) this.status.textContent = "";
          this.recalculate();
        };
        this.onClick = (event) => {
          const add = event.target.closest("[data-wc-add]");
          const remove = event.target.closest("[data-wc-remove]");
          if (this.busy || this.constructor.pendingAdd || this.constructor.uncertainAdd) return;
          if (add) {
            if (this.rows.children.length >= 20) {
              this.showError(this.strings.rows_limit);
              return;
            }
            this.appendRow({ length: String(this.getVariant()?.minimum || 1), pieces: "1" });
            this.recalculate();
            this.rows.lastElementChild.querySelector("[data-wc-length]").focus();
          } else if (remove && this.rows.children.length > 1) {
            const row = remove.closest(".wirenco-cut-row");
            const focusRow = row.previousElementSibling || row.nextElementSibling;
            row.remove();
            this.showError();
            this.recalculate();
            focusRow?.querySelector("[data-wc-length]")?.focus();
          }
        };
        this.onVariantChange = () => this.recalculate();
        this.addEventListener("input", this.onInput);
        this.addEventListener("change", this.onInput);
        this.addEventListener("click", this.onClick);
        document.addEventListener("product:variant-change", this.onVariantChange);
        this.initialized = true;
        this.recalculate();
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        this.saveRows();
        this.removeEventListener("input", this.onInput);
        this.removeEventListener("change", this.onInput);
        this.removeEventListener("click", this.onClick);
        document.removeEventListener("product:variant-change", this.onVariantChange);
        this.initialized = false;
      }

      format(template, values) {
        return Object.entries(values).reduce(
          (text, [key, value]) => text.replace(
            new RegExp(`{{\\s*${key}\\s*}}|__${key.toUpperCase()}__`, "g"),
            () => String(value),
          ),
          template || "",
        );
      }

      getVariant() {
        const id = this.form.querySelector('[name="id"]')?.value;
        return this.config.variants.find((variant) => String(variant.id) === String(id));
      }

      appendRow(values) {
        const fragment = this.template.content.cloneNode(true);
        fragment.querySelector("[data-wc-length]").value = values.length;
        fragment.querySelector("[data-wc-pieces]").value = values.pieces;
        this.rows.appendChild(fragment);
      }

      saveRows() {
        if (!this.rows) return;
        this.constructor.selections.set(
          this.selectionKey,
          Array.from(this.rows.querySelectorAll(".wirenco-cut-row"), (row) => ({
            length: row.querySelector("[data-wc-length]").value,
            pieces: row.querySelector("[data-wc-pieces]").value,
          })),
        );
      }

      readPlan() {
        const variant = this.getVariant();
        const minimum = Math.max(1, Number(variant?.minimum) || 1);
        const increment = Math.max(1, Number(variant?.increment) || 1);
        const maximum = Number(variant?.maximum) || null;
        const cuts = [];
        let feet = 0;
        let pieces = 0;
        for (const row of this.rows.querySelectorAll(".wirenco-cut-row")) {
          const lengthInput = row.querySelector("[data-wc-length]");
          const piecesInput = row.querySelector("[data-wc-pieces]");
          const length = Number(lengthInput.value);
          const count = Number(piecesInput.value);
          if (
            !Number.isSafeInteger(length) || length < minimum ||
            (length - minimum) % increment !== 0 || (maximum && length > maximum)
          ) {
            return { error: this.strings.invalid, input: lengthInput };
          }
          if (!Number.isSafeInteger(count) || count < 1) {
            return { error: this.strings.invalid, input: piecesInput };
          }
          pieces += count;
          if (pieces > 50) return { error: this.strings.too_many, input: piecesInput };
          feet += length * count;
          if (!Number.isSafeInteger(feet)) return { error: this.strings.invalid, input: lengthInput };
          for (let index = 0; index < count; index += 1) cuts.push(length);
        }
        if (!cuts.length) return { error: this.strings.invalid };
        return { cuts, feet, pieces, variant };
      }

      recalculate() {
        if (!this.form || !this.config) return;
        const variant = this.getVariant();
        const minimum = Math.max(1, Number(variant?.minimum) || 1);
        const increment = Math.max(1, Number(variant?.increment) || 1);
        const maximum = Number(variant?.maximum) || null;
        this.rows.querySelectorAll(".wirenco-cut-row").forEach((row, index) => {
          const length = row.querySelector("[data-wc-length]");
          const pieces = row.querySelector("[data-wc-pieces]");
          length.min = String(minimum);
          length.step = String(increment);
          if (maximum) length.max = String(maximum);
          else length.removeAttribute("max");
          pieces.max = "50";
          const rowTotal = Number(length.value) * Number(pieces.value);
          row.querySelector("[data-wc-row-total]").textContent = this.format(
            this.strings.length_value,
            { length: Number.isSafeInteger(rowTotal) && rowTotal > 0 ? rowTotal : 0 },
          );
          row.querySelector("[data-wc-remove]").setAttribute(
            "aria-label", `${this.strings.remove_length} ${index + 1}`,
          );
        });
        const plan = this.readPlan();
        const feet = plan.feet || 0;
        const pieces = plan.pieces || 0;
        this.querySelector("[data-wc-total-pieces]").textContent = String(pieces);
        this.querySelector("[data-wc-total-feet]").textContent = this.format(this.strings.length_value, { length: feet });
        this.querySelector("[data-wc-price]").textContent = this.money.format(((variant?.price || 0) * feet) / 100);
        const quantity = this.querySelector("[data-wc-total-quantity]");
        if (quantity.value !== String(feet)) {
          quantity.value = String(feet);
          quantity.dispatchEvent(new Event("change", { bubbles: true }));
        }
        this.saveRows();
        this.syncControls();
      }

      syncControls() {
        const locked = this.busy || this.constructor.pendingAdd || this.constructor.uncertainAdd;
        this.querySelectorAll("input:not([type=hidden]), button").forEach((control) => {
          control.disabled = Boolean(locked);
        });
        if (!locked) {
          this.rows.querySelectorAll("[data-wc-remove]").forEach((button) => {
            button.disabled = this.rows.children.length <= 1;
            button.hidden = this.rows.children.length <= 1;
          });
          this.querySelector("[data-wc-add]").disabled = this.rows.children.length >= 20;
        }
        const available = this.getVariant()?.available === true;
        if (this.submit) {
          this.submit.disabled = Boolean(locked || !available);
          if (locked) this.submit.setAttribute("aria-disabled", "true");
          else this.submit.removeAttribute("aria-disabled");
          const label = this.submit.querySelector("span");
          if (label && available) label.textContent = this.strings.add_to_cart;
        }
        this.setAttribute("aria-busy", this.busy ? "true" : "false");
      }

      syncAllControls() {
        document.querySelectorAll("wirenco-cut-picker").forEach((picker) => {
          if (picker.initialized) picker.syncControls();
        });
      }

      showError(message) {
        this.error.hidden = !message;
        this.error.textContent = message || "";
      }

      route(path) {
        return `${window.Shopify?.routes?.root || "/"}${path}`;
      }

      async request(path, body) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 20000);
        try {
          const response = await fetch(this.route(path), {
            method: body ? "POST" : "GET",
            credentials: "same-origin",
            cache: "no-store",
            headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) },
            ...(body ? { body: JSON.stringify(body) } : {}),
            signal: controller.signal,
          });
          const data = await response.json();
          if (!response.ok || data.status || data.errors) {
            const message = typeof data.description === "string" ? data.description :
              typeof data.errors === "string" ? data.errors : this.strings.add_error;
            const error = new Error(message);
            error.confirmedResponse = true;
            throw error;
          }
          return data;
        } finally {
          clearTimeout(timer);
        }
      }

      makeId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        const random = new Uint32Array(4);
        window.crypto.getRandomValues(random);
        return `${Date.now().toString(36)}-${Array.from(random, (value) => value.toString(36)).join("-")}`;
      }

      buildItems(plan, batch) {
        const formData = new FormData(this.form);
        const properties = {};
        for (const [name, value] of formData.entries()) {
          const match = name.match(/^properties\[(.+)\]$/);
          if (match && typeof value === "string") properties[match[1]] = value;
        }
        const sellingPlan = formData.get("selling_plan");
        return plan.cuts.map((length) => ({
          id: Number(plan.variant.id),
          quantity: length,
          properties: {
            ...properties,
            "Cut length": this.format(this.strings.length_value, { length }),
            _wirenco_cut_id: this.makeId(),
            _wirenco_cut_batch: batch,
          },
          ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
        }));
      }

      batchIsComplete(cart, items, batch) {
        const actual = cart.items.filter((item) => item.properties?._wirenco_cut_batch === batch);
        if (actual.length !== items.length) return false;
        return items.every((intended) => {
          const matching = actual.filter((item) => item.properties?._wirenco_cut_id === intended.properties._wirenco_cut_id);
          return matching.length === 1 && matching[0].quantity === intended.quantity &&
            String(matching[0].variant_id || matching[0].id) === String(intended.id) &&
            matching[0].properties["Cut length"] === intended.properties["Cut length"];
        });
      }

      async removeBatch(cart, batch) {
        for (let count = 0; count < 51; count += 1) {
          const line = cart.items.find((item) => item.properties?._wirenco_cut_batch === batch);
          if (!line) return cart;
          cart = await this.request("cart/change.js", { id: line.key, quantity: 0 });
        }
        throw new Error(this.strings.review_cart);
      }

      async refreshCart(productForm, response) {
        const mini = productForm.minicart || document.querySelector("mini-cart");
        if (mini) {
          const sections = mini.getSectionsToRender();
          if (response?.sections && sections.every((section) => typeof response.sections[section.section] === "string")) {
            mini.renderContents(response);
          } else {
            await mini.updateSectionContents();
          }
        }
        for (const cart of document.querySelectorAll("cart-items")) await cart.updateSectionContents();
        return mini;
      }

      async performAdd(productForm, plan, items, batch) {
        let cart = await this.request("cart.js");
        const existingFeet = cart.items.reduce((sum, item) =>
          sum + (String(item.variant_id || item.id) === String(plan.variant.id) ? item.quantity : 0), 0);
        if (plan.variant.inventory_management && plan.variant.inventory_policy === "deny") {
          const available = Math.max(0, Number(plan.variant.inventory_quantity) - existingFeet);
          if (plan.feet > available) throw new Error(this.format(this.strings.stock, { available }));
        }
        if (plan.variant.maximum && existingFeet + plan.feet > Number(plan.variant.maximum)) {
          throw new Error(this.format(this.strings.stock, { available: Math.max(0, Number(plan.variant.maximum) - existingFeet) }));
        }
        const mini = productForm.minicart || document.querySelector("mini-cart");
        const sections = mini ? mini.getSectionsToRender().map((section) => section.section).slice(0, 5) : [];
        let response;
        let addError;
        try {
          response = await this.request("cart/add.js", {
            items,
            ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
          });
        } catch (error) {
          addError = error;
        }
        try {
          cart = await this.request("cart.js");
          if (!this.batchIsComplete(cart, items, batch)) {
            cart = await this.removeBatch(cart, batch);
            if (addError && !addError.confirmedResponse) {
              this.constructor.uncertainAdd = true;
              throw new Error(this.strings.review_cart);
            }
            try { await this.refreshCart(productForm); } catch (_) {}
            const error = new Error(addError?.message || this.strings.add_error);
            error.batchCleaned = true;
            throw error;
          }
        } catch (error) {
          if (!error.batchCleaned) {
            this.constructor.uncertainAdd = true;
            throw new Error(this.strings.review_cart);
          }
          throw error;
        }
        const addedItems = cart.items.filter((item) => item.properties?._wirenco_cut_batch === batch);
        try {
          await this.refreshCart(productForm, addError ? null : response);
        } catch (_) {
          window.location.assign(window.routes?.cart_url || this.route("cart"));
          return;
        }
        document.dispatchEvent(new CustomEvent("cart:item-add", {
          detail: { items: addedItems, context: productForm.eventContext },
        }));
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          try {
            publish(PUB_SUB_EVENTS.cartItemAdd, { source: productForm.eventContext, items: addedItems });
          } catch (_) {}
        }
        if (this.status) this.status.textContent = this.format(this.strings.added, { pieces: plan.pieces, feet: plan.feet });
        if (window.themeSettings?.redirectToCart || !mini) {
          window.location.assign(window.routes?.cart_url || this.route("cart"));
        } else if (productForm.quickViewDrawer) {
          productForm.quickViewDrawer.close();
          document.body.classList.add("drawer-open");
          setTimeout(() => mini.open(), 200);
        } else {
          try {
            mini.open(this.submit);
          } catch (_) {
            window.location.assign(window.routes?.cart_url || this.route("cart"));
          }
        }
      }

      async addToCart(productForm) {
        if (!this.initialized || this.busy || this.constructor.pendingAdd || this.constructor.uncertainAdd) return;
        this.showError();
        productForm.handleErrorMessage();
        const plan = this.readPlan();
        if (plan.error) {
          this.showError(plan.error);
          plan.input?.focus();
          plan.input?.reportValidity();
          return;
        }
        if (!plan.variant?.available) {
          this.showError(this.strings.unavailable);
          return;
        }
        if (!this.form.reportValidity()) return;
        const batch = this.makeId();
        const items = this.buildItems(plan, batch);
        this.busy = true;
        this.constructor.pendingAdd = true;
        this.submit.classList.add("loading");
        this.submit.querySelector(".button-overlay-spinner")?.classList.remove("hidden");
        this.syncAllControls();
        const operation = () => this.performAdd(productForm, plan, items, batch);
        const queued = (window.wirencoCutCartQueue || Promise.resolve()).catch(() => {}).then(operation);
        window.wirencoCutCartQueue = queued;
        try {
          await queued;
        } catch (error) {
          const activePicker = Array.from(document.querySelectorAll("wirenco-cut-picker"))
            .find((picker) => picker.selectionKey === this.selectionKey) || this;
          activePicker.showError(error.message || this.strings.add_error);
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "wirenco-cut-picker", productVariantId: plan.variant.id,
              errors: error.message, message: error.message,
            });
          }
        } finally {
          this.busy = false;
          this.constructor.pendingAdd = false;
          this.submit.classList.remove("loading");
          this.submit.querySelector(".button-overlay-spinner")?.classList.add("hidden");
          this.syncAllControls();
        }
      }
    },
  );
}
